import React, { useEffect, useState } from 'react'
import { supabase } from './supabase'

// ============================================================================
// HomePros — FeaturedListings (shared, embeddable listings strip)
// ============================================================================
// Single source of truth for the featured-listings UI: the card grid, the
// detail modal, and the lead-capture form. Renders standalone on the public
// /listings page (via ListingsPage.jsx) and is designed to be dropped onto
// other pages (e.g. per-agent funnels) without modification.
//
// Self-contained:
//   - Does its own fetch from the get-featured-listings edge function.
//   - Carries its own hp-l- <style> block, so cards look identical wherever
//     it renders (including pages that don't define the .hp-page globals).
//   - Renders nothing when the fetch returns zero listings (hides itself),
//     or the caller's emptyFallback when one is given. While the edge
//     function is paused (FEATURED_LISTINGS_PAUSED) it always returns zero.
//
// Per-funnel routing seam: the `source` prop threads
//   FeaturedListings -> ListingModal -> LeadForm -> submitLead
// and becomes the `source` value on the listing_leads insert. When omitted it
// defaults to 'HomePros Hub Listings' (the standalone broker behavior), so
// /listings keeps routing every lead to the broker via the matching FUB Lead
// Flow rule. A funnel passes its own row.source_tag to reroute leads to the
// embedding agent instead.
//
// Data source: edge function returning highest-price-first, max 5:
//   GET https://oeeanuemukqkwhtlkqij.supabase.co/functions/v1/get-featured-listings
//   -> { count, listings: [{ mls, price, address, city, beds, baths, sqft,
//                            photos, photo, gallery, office, ... }] }
// ============================================================================

export const BRAND = {
  navy: '#1d2e42',
  black: '#000000',
  white: '#ffffff',
  blue: '#147efb',
  gold: '#fdbf00',
}

const DEFAULT_SOURCE = 'HomePros Hub Listings'

const FEATURED_URL =
  'https://oeeanuemukqkwhtlkqij.supabase.co/functions/v1/get-featured-listings'

function formatPrice(n) {
  const num = Number(n)
  if (!isFinite(num) || num <= 0) return ''
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(num)
}

function formatSqft(n) {
  const num = Number(n)
  if (!isFinite(num) || num <= 0) return ''
  return new Intl.NumberFormat('en-US').format(num)
}

// ─── LISTING CARD ───────────────────────────────────────────────────────────
function ListingCard({ listing, isHero, onOpen }) {
  const price = formatPrice(listing?.price)
  const sqft = formatSqft(listing?.sqft)
  const beds = listing?.beds
  const baths = listing?.baths
  const photoCount = Number(listing?.photos) || 0
  const photoUrl = listing?.photo || ''
  // Edge function returns `office` (real brokerage string, e.g. "HomePros" or
  // "HomePros Advantage"). Fallback to 'HomePros' only if missing.
  const office = listing?.office || 'HomePros'

  // The feed's UnparsedAddress already includes the city, so we only render
  // `address`. `city` is still received and can be used elsewhere (e.g. detail
  // modal metadata or future filters); just not appended here.
  const addressLine = listing?.address || ''

  // Meta row: only render pieces that exist, separated by middots.
  const metaParts = []
  if (beds) metaParts.push(`${beds} bd`)
  if (baths) metaParts.push(`${baths} ba`)
  if (sqft) metaParts.push(`${sqft} sqft`)

  const handleKeyDown = (e) => {
    // Enter and Space activate the card, matching native button behavior so
    // keyboard users can open the detail modal without the mouse.
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (onOpen) onOpen(listing)
    }
  }

  return (
    <article
      className={`hp-l-card ${isHero ? 'hp-l-card-hero' : ''}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen && onOpen(listing)}
      onKeyDown={handleKeyDown}
      aria-label={`View ${listing?.address || 'listing'} details`}
    >
      <div
        className="hp-l-photo"
        style={photoUrl ? { backgroundImage: `url(${photoUrl})` } : { background: '#1a2434' }}
        aria-hidden="true"
      />
      <div className="hp-l-scrim" aria-hidden="true" />

      {isHero ? (
        <div className="hp-l-badge hp-l-badge-top">
          <span className="hp-l-badge-dot" /> Top listing
        </div>
      ) : (
        <div className="hp-l-badge hp-l-badge-forsale">For sale</div>
      )}

      {photoCount > 0 && (
        <div className="hp-l-photo-pill">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M4 8h3l2-3h6l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2z"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="1.8" />
          </svg>
          <span>{photoCount}</span>
        </div>
      )}

      <div className="hp-l-overlay">
        {price && <div className="hp-l-price">{price}</div>}
        <div className="hp-l-office">{office}</div>
        {addressLine && <div className="hp-l-addr">{addressLine}</div>}
        {metaParts.length > 0 && (
          <div className="hp-l-meta">
            {metaParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="hp-l-meta-sep">·</span>}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Hover CTA — visual affordance. Clicks bubble to the article, which
          owns the actual onOpen handler. Kept tabIndex={-1} so keyboard
          users activate the parent article (the real button), not this. */}
      <button type="button" className="hp-l-cta" tabIndex={-1}>
        View property
      </button>
    </article>
  )
}

// ─── LISTINGS GRID ──────────────────────────────────────────────────────────
function ListingsGrid({ listings, onSelect }) {
  if (!listings || listings.length === 0) return null
  const [hero, ...rest] = listings
  return (
    <div className="hp-l-grid">
      <ListingCard listing={hero} isHero onOpen={onSelect} />
      {rest.map((l) => (
        <ListingCard key={l.mls || `${l.address}-${l.price}`} listing={l} onOpen={onSelect} />
      ))}
    </div>
  )
}

// ─── LEAD SUBMIT ────────────────────────────────────────────────────────────
// Inserts the captured lead into Supabase, then fires the FUB-routing edge
// function fire-and-forget. Mirrors OpenHouseLanding.jsx — insert is the
// source of truth (throws on failure so the form re-enables submit and the
// buyer can retry), FUB push is best-effort (admin can manually retry from a
// future Leads admin tab if it fails).
//
// `source` is threaded from the embedding page (FeaturedListings `source`
// prop). On standalone /listings it resolves to 'HomePros Hub Listings' and
// all leads route to the broker; on a per-agent funnel the funnel passes its
// own source_tag so FUB Lead Flows assign to the embedding agent instead.
async function submitLead(payload) {
  // Client-generated id: RETURNING would require a SELECT policy anon doesn't
  // have. Same pattern as ProtectLanding.
  const id = crypto.randomUUID()
  const { error } = await supabase
    .from('listing_leads')
    .insert({
      id,
      name: payload.name,
      phone: payload.phone,
      // Email is optional on the form — coerce undefined → null so the DB
      // gets an explicit NULL rather than relying on column defaults.
      email: payload.email || null,
      mls: payload.mls,
      address: payload.address,
      office: payload.office,
      source: payload.source || DEFAULT_SOURCE,
    })
  if (error) {
    console.error('[listing_leads] insert failed:', error)
    throw error
  }

  // Fire-and-forget FUB push. A FUB failure must NOT bubble up — the row is
  // already in listing_leads (the source of truth), retryable later. Soft-log
  // both invoke failures and function-returned errors so they surface in
  // browser console / Sentry without breaking the user-facing success state.
  supabase.functions
    .invoke('send-listing-lead-to-fub', { body: { lead_id: id } })
    .then(({ data, error: fnErr }) => {
      if (fnErr) console.error('[fub push] invoke failed:', fnErr)
      else if (data?.error) console.error('[fub push] function returned error:', data.error)
    })
    .catch((e) => console.error('[fub push] threw:', e))
}

// Disposable email blocklist — domains commonly used to receive a lead magnet
// then vanish. Lowercased on compare. Extend cautiously; false positives here
// look identical to a typo from the user's perspective.
const DISPOSABLE_EMAIL_DOMAINS = [
  'mailinator.com', 'guerrillamail.com', '10minutemail.com', 'tempmail.com',
  'throwaway.email', 'yopmail.com', 'trashmail.com', 'sharklasers.com',
  'getnada.com', 'temp-mail.org', 'fakeinbox.com', 'maildrop.cc',
]

// Returns an errors object: { name: '', phone: '', email: '' }. Empty string =
// field is OK. Caller checks `Object.values(errors).some(Boolean)` to decide
// whether to bail.
function validateLead({ name, phone, email }) {
  const errors = { name: '', phone: '', email: '' }

  // NAME — trimmed length >= 2 and not just a single repeated character.
  const trimmedName = name.trim()
  const nameChars = new Set(trimmedName.toLowerCase())
  if (trimmedName.length < 2 || nameChars.size === 1) {
    errors.name = 'Please enter your name.'
  }

  // PHONE — strip to digits, must be exactly 10, reject obvious junk.
  const digits = phone.replace(/\D/g, '')
  const allSame = /^(\d)\1{9}$/.test(digits)
  const sequential = digits === '1234567890' || digits === '0987654321'
  if (digits.length !== 10 || allSame || sequential) {
    errors.phone = 'Please enter a valid 10-digit phone number.'
  }

  // EMAIL — optional. Skip entirely when blank; otherwise check shape + domain.
  const trimmedEmail = email.trim()
  if (trimmedEmail) {
    const shapeOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)
    if (!shapeOk) {
      errors.email = 'Please enter a valid email address.'
    } else {
      const domain = trimmedEmail.split('@')[1].toLowerCase()
      if (DISPOSABLE_EMAIL_DOMAINS.includes(domain)) {
        errors.email = 'Please use a permanent email address.'
      }
    }
  }

  return errors
}

// ─── LEAD FORM ──────────────────────────────────────────────────────────────
function LeadForm({ listing, source, onSuccess }) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  // Per-field errors keyed by field name. Empty string = no error on that
  // field. We don't run validation on every keystroke; it runs at submit time
  // and clears per-field as the user edits the offending input.
  const [errors, setErrors] = useState({ name: '', phone: '', email: '' })
  // Double-submit guard: once submission is in-flight, the button and Enter
  // handler both short-circuit. Once 'submitted' flips true, the form is
  // permanently disabled — a lead can only be captured once per modal open.
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  // Button-enabled gate stays "looks filled out" — same friendly UX as before.
  // Real validation runs inside handleSubmit so a user typing junk doesn't
  // get yelled at every keystroke; they find out when they hit submit.
  const nameLooksFilled = !!name.trim()
  const phoneLooksFilled = !!phone.trim()
  const canSubmit = nameLooksFilled && phoneLooksFilled && !submitting && !submitted

  const clearError = (field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev))
  }

  const handleSubmit = async () => {
    if (submitting || submitted) return // hard guard against double-fire
    const next = validateLead({ name, phone, email })
    if (next.name || next.phone || next.email) {
      setErrors(next)
      return
    }
    setErrors({ name: '', phone: '', email: '' })
    setSubmitting(true)
    try {
      // Normalize phone to bare digits so downstream (FUB) receives a clean
      // 10-digit number regardless of how the user typed it.
      const phoneDigits = phone.replace(/\D/g, '')
      const trimmedEmail = email.trim()
      await submitLead({
        name: name.trim(),
        phone: phoneDigits,
        email: trimmedEmail || undefined,
        mls: listing?.mls,
        address: listing?.address,
        office: listing?.office || 'HomePros',
        source,
      })
      setSubmitted(true)
      if (onSuccess) onSuccess()
    } catch (err) {
      console.error('[lead submit] failed:', err)
      // Re-enable submit so the user can retry. Once the real FUB call lands
      // here, surface an inline error message in this branch instead of just
      // logging.
      setSubmitting(false)
    }
  }

  const onEnterSubmit = (e) => {
    // Enter on any field submits, matching how forms feel everywhere else.
    // Mirrors handleSubmit's gating exactly — same guards, same validation.
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
  }

  const disabled = submitting || submitted

  return (
    <div className="hp-l-form">
      <div className="hp-l-form-field">
        <input
          className="hp-l-form-input"
          value={name}
          onChange={(e) => { setName(e.target.value); clearError('name') }}
          onKeyDown={onEnterSubmit}
          placeholder="Full name *"
          autoComplete="name"
          disabled={disabled}
        />
        {errors.name && <div className="hp-l-form-err">{errors.name}</div>}
      </div>

      <div className="hp-l-form-field">
        <input
          className="hp-l-form-input"
          value={phone}
          onChange={(e) => { setPhone(e.target.value); clearError('phone') }}
          onKeyDown={onEnterSubmit}
          placeholder="Phone *"
          autoComplete="tel"
          inputMode="tel"
          disabled={disabled}
        />
        {errors.phone && <div className="hp-l-form-err">{errors.phone}</div>}
      </div>

      <div className="hp-l-form-field">
        <input
          className="hp-l-form-input"
          value={email}
          onChange={(e) => { setEmail(e.target.value); clearError('email') }}
          onKeyDown={onEnterSubmit}
          placeholder="Email (optional)"
          autoComplete="email"
          inputMode="email"
          disabled={disabled}
        />
        {errors.email && <div className="hp-l-form-err">{errors.email}</div>}
      </div>

      <button
        type="button"
        className="hp-l-form-submit"
        onClick={handleSubmit}
        disabled={!canSubmit}
        aria-busy={submitting}
      >
        {submitted ? 'Submitted ✓' : submitting ? 'Submitting…' : 'Unlock photos & request showing'}
      </button>
    </div>
  )
}

// ─── LISTING MODAL ──────────────────────────────────────────────────────────
function ListingModal({ listing, onClose, source }) {
  // Modal lifecycle: 'gated' (initial — locked tease + primary CTA),
  // 'form_open' (lead form visible), 'submitted' (gallery unlocked + success).
  const [status, setStatus] = useState('gated')
  // Which photo is shown as the large primary image. Only navigable after
  // unlock; before unlock the primary stays on gallery[0] regardless of this
  // value (the gating is enforced in the JSX, not by clamping state).
  const [activePhoto, setActivePhoto] = useState(0)

  // Body scroll lock — restore previous overflow on unmount so we don't
  // clobber a value some other component might depend on.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prevOverflow }
  }, [])

  // Keyboard arrows control the gallery only after unlock. Lives here (above
  // the early-return) to satisfy react-hooks/rules-of-hooks; derives its own
  // unlock + length flags from the source props rather than the const block
  // below, which executes only after this hook is registered.
  useEffect(() => {
    const isUnlockedNow = status === 'submitted'
    const gallerySrc = Array.isArray(listing?.gallery) ? listing.gallery : []
    const len = gallerySrc.length
    if (!isUnlockedNow || len <= 1) return undefined
    const onKey = (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        setActivePhoto((i) => (i + 1) % len)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setActivePhoto((i) => (i - 1 + len) % len)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [status, listing])

  if (!listing) return null

  const price = formatPrice(listing?.price)
  const sqft = formatSqft(listing?.sqft)
  const beds = listing?.beds
  const baths = listing?.baths
  const photoCount = Number(listing?.photos) || 0
  const photoUrl = listing?.photo || ''
  // Real full-array gallery from the edge function. Defensive: if absent we
  // fall back to the single `photo` so the modal never blanks out.
  const gallery = Array.isArray(listing?.gallery) ? listing.gallery : []
  const galleryLen = gallery.length
  // Tease: first six real photos. Listings with fewer than 6 photos render
  // only as many as exist — no empty placeholder tiles.
  const teaseList = galleryLen > 0 ? gallery.slice(0, 6) : (photoUrl ? [photoUrl] : [])
  // Wall count uses gallery length when available so what we promise matches
  // what we unlock. Falls back to photoCount only for legacy/stale shapes.
  const totalPhotos = galleryLen || photoCount

  const addressLine = listing?.address || ''
  const cityLine = listing?.city || ''
  const office = listing?.office || 'HomePros'
  const isUnlocked = status === 'submitted'

  // Active primary photo URL. activePhoto drives the primary in BOTH states
  // (pre-unlock the buyer can click among the 6 teaser thumbs; post-unlock
  // arrows + thumbs cover the whole gallery). Clamp to gallery range as a
  // belt-and-suspenders guard against any drift.
  const primaryPhotoUrl = galleryLen > 0
    ? gallery[Math.min(activePhoto, galleryLen - 1)]
    : photoUrl

  const metaParts = []
  if (beds) metaParts.push(`${beds} bd`)
  if (baths) metaParts.push(`${baths} ba`)
  if (sqft) metaParts.push(`${sqft} sqft`)

  const openForm = () => {
    if (status === 'gated') setStatus('form_open')
  }

  // Wrap-around gallery navigation. No-op when there's nothing to navigate.
  const nextPhoto = () => {
    if (galleryLen <= 1) return
    setActivePhoto((i) => (i + 1) % galleryLen)
  }
  const prevPhoto = () => {
    if (galleryLen <= 1) return
    setActivePhoto((i) => (i - 1 + galleryLen) % galleryLen)
  }


  return (
    <div
      className="hp-l-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Listing details"
    >
      <div className="hp-l-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="hp-l-modal-close"
          onClick={onClose}
          aria-label="Close listing details"
        >
          ×
        </button>

        {/* Primary photo with overlay arrows + counter. The wrap is the
            positioned container so arrows can absolutely-position over the
            background-image div underneath. */}
        <div className="hp-l-modal-photo-wrap">
          <div
            className="hp-l-modal-photo"
            style={primaryPhotoUrl ? { backgroundImage: `url(${primaryPhotoUrl})` } : { background: '#1a2434' }}
            aria-hidden="true"
          />
          {isUnlocked && galleryLen > 1 && (
            <>
              <button
                type="button"
                className="hp-l-modal-arrow hp-l-modal-arrow-left"
                onClick={prevPhoto}
                aria-label="Previous photo"
              >
                ‹
              </button>
              <button
                type="button"
                className="hp-l-modal-arrow hp-l-modal-arrow-right"
                onClick={nextPhoto}
                aria-label="Next photo"
              >
                ›
              </button>
            </>
          )}
          {isUnlocked && galleryLen > 0 && (
            <div className="hp-l-modal-photo-counter">
              {activePhoto + 1} / {galleryLen}
            </div>
          )}
        </div>

        {/* Photo tease: first 6 real photos from gallery, then the wall.
            All six thumbnails are fully visible AND clickable in both states —
            clicking one sets it as the primary photo and gets the blue active
            border. The wall is the explicit gate ("See all N photos →") that
            opens the lead form; full gallery sweep (arrows + counter) is the
            post-unlock reward. */}
        {teaseList.length > 0 && (
          <div className={`hp-l-modal-thumbs ${isUnlocked ? 'is-unlocked' : ''}`}>
            {teaseList.map((url, i) => {
              const active = i === activePhoto
              const cls = ['hp-l-thumb', active ? 'is-active' : ''].filter(Boolean).join(' ')
              return (
                <button
                  key={i}
                  type="button"
                  className={cls}
                  style={{ backgroundImage: `url(${url})` }}
                  onClick={() => setActivePhoto(i)}
                  aria-label={`View photo ${i + 1} of ${totalPhotos || teaseList.length}`}
                />
              )
            })}
            {totalPhotos > 0 && (
              <button
                type="button"
                className="hp-l-wall"
                onClick={openForm}
                disabled={isUnlocked}
              >
                <span className="hp-l-wall-label">
                  {isUnlocked
                    ? `${totalPhotos} photos`
                    : `See all ${totalPhotos} photos →`}
                </span>
              </button>
            )}
          </div>
        )}

        {/* Details */}
        <div className="hp-l-modal-details">
          {price && <div className="hp-l-modal-price">{price}</div>}
          <div className="hp-l-modal-office">{office}</div>
          {addressLine && (
            <div className="hp-l-modal-addr">
              {addressLine}
              {cityLine && <span className="hp-l-modal-city"> · {cityLine}</span>}
            </div>
          )}
          {metaParts.length > 0 && (
            <div className="hp-l-modal-meta">
              {metaParts.map((part, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <span className="hp-l-modal-meta-sep">·</span>}
                  <span>{part}</span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Stage-driven CTA / form / success */}
        {status === 'gated' && (
          <button
            type="button"
            className="hp-l-modal-cta"
            onClick={openForm}
          >
            Request a private showing
          </button>
        )}

        {status === 'form_open' && (
          <LeadForm
            listing={listing}
            source={source}
            onSuccess={() => setStatus('submitted')}
          />
        )}

        {status === 'submitted' && (
          <div className="hp-l-success">
            <div className="hp-l-success-mark" aria-hidden="true">✓</div>
            <div className="hp-l-success-title">An agent will reach out shortly.</div>
            <div className="hp-l-success-sub">
              You'll hear from a HomePros agent within 24 hours about a private showing.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── FEATURED LISTINGS (public, embeddable) ─────────────────────────────────
// Props:
//   source  — routing tag stamped on the listing_leads insert (defaults to the
//             broker tag, preserving standalone /listings behavior).
//   heading — optional ReactNode rendered above the grid. Gated by the same
//             "has listings" check as the grid, so on an empty fetch the
//             heading hides too (matches the old whole-section gate).
//   framed  — when true, wraps heading + grid in the /listings section/container
//             chrome (.hp-l-section > .hp-container). Funnels leave this false
//             to drop the bare grid into their own layout.
//   frame   — 2026-10-10: optional (content) => ReactNode. The embedding
//             page's own wrapper (band, card, heading), rendered only when
//             there are listings, so an empty or paused fetch leaves no empty
//             box behind. Takes precedence over `framed`.
//   emptyFallback — 2026-10-10: optional ReactNode shown instead of nothing
//             when the fetch finishes with no listings (paused, empty or
//             failed). Never shown while loading. No grid, modal or lead form
//             renders with it.
export function FeaturedListings({ source = DEFAULT_SOURCE, heading = null, framed = false, frame = null, emptyFallback = null }) {
  const [status, setStatus] = useState('loading') // 'loading' | 'ready' | 'empty' | 'error'
  const [listings, setListings] = useState([])
  // null = no modal; non-null = the listing whose detail panel is open.
  const [selected, setSelected] = useState(null)

  useEffect(() => {
    let cancelled = false
    fetch(FEATURED_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        if (cancelled) return
        const arr = Array.isArray(data?.listings) ? data.listings : []
        setListings(arr)
        setStatus(arr.length === 0 ? 'empty' : 'ready')
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[get-featured-listings] fetch failed:', err)
        setListings([])
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Hide the entire strip (heading included) when there's nothing to show.
  // Spec: "If the fetch returns zero listings, render nothing." We render the
  // <style> only alongside content, so an empty/loading state injects nothing.
  const showSection = status === 'ready' && listings.length > 0
  if (!showSection) return status === 'loading' ? null : emptyFallback

  const body = (
    <>
      {heading}
      <ListingsGrid listings={listings} onSelect={setSelected} />
    </>
  )

  return (
    <>
      <style>{css}</style>
      {frame ? (
        frame(body)
      ) : framed ? (
        <section className="hp-l-section">
          <div className="hp-container">{body}</div>
        </section>
      ) : (
        body
      )}
      {selected && (
        <ListingModal listing={selected} onClose={() => setSelected(null)} source={source} />
      )}
    </>
  )
}

// ─── STYLES ─────────────────────────────────────────────────────────────────
// The hp-l- card / grid / modal / form CSS plus the section-heading styles,
// so the strip is self-contained. A scoped box-sizing + font reset means it
// renders identically whether it's inside the /listings page shell (.hp-page)
// or embedded on a page (e.g. a funnel) that doesn't define those globals.
const css = `
  :root {
    --hp-navy: ${BRAND.navy};
    --hp-black: ${BRAND.black};
    --hp-white: ${BRAND.white};
    --hp-blue: ${BRAND.blue};
    --hp-gold: ${BRAND.gold};
    --hp-ink: #0a0a0a;
    --hp-ink-soft: #4a4a4a;
    --hp-line: #e8e8e8;
    --hp-bg-soft: #f7f7f5;
  }

  /* Self-contained reset — additive (the /listings shell already sets these
     via .hp-page *, so it stays identical there; embeds get them for free). */
  .hp-l-section, .hp-l-section *,
  .hp-l-grid, .hp-l-grid *,
  .hp-l-modal-backdrop, .hp-l-modal-backdrop * { box-sizing: border-box; }
  .hp-l-grid, .hp-l-modal-backdrop {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }

  .hp-container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

  /* ─── SECTION HEADER ──────────────────────────────────────────────────── */
  .hp-l-section {
    padding: 56px 0 96px;
    background: var(--hp-white);
  }
  .hp-l-section-head { margin-bottom: 32px; }
  .hp-eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 600;
    letter-spacing: 0.08em; text-transform: uppercase;
    color: var(--hp-ink-soft); margin-bottom: 16px;
  }
  .hp-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: var(--hp-gold);
    display: inline-block;
  }
  .hp-l-pulse {
    position: relative;
    box-shadow: 0 0 0 0 rgba(253,191,0,0.7);
    animation: hpLPulse 1.8s infinite;
  }
  @keyframes hpLPulse {
    0%   { box-shadow: 0 0 0 0   rgba(253,191,0,0.55); }
    70%  { box-shadow: 0 0 0 10px rgba(253,191,0,0); }
    100% { box-shadow: 0 0 0 0   rgba(253,191,0,0); }
  }
  .hp-l-h1 {
    font-size: clamp(32px, 5vw, 48px);
    font-weight: 900;
    letter-spacing: -0.01em;
    line-height: 1.05;
    color: var(--hp-navy);
    margin: 0 0 10px;
  }
  .hp-l-tagline {
    font-size: 16px;
    color: var(--hp-ink-soft);
    margin: 0;
    max-width: 560px;
  }

  /* ─── GRID ────────────────────────────────────────────────────────────── */
  /* Desktop: 4-col grid; hero spans 2x2, standard cards fill the remaining
     2 columns. Tablet: 2-col, hero spans the full row. Mobile: stacked. */
  .hp-l-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-auto-rows: 220px;
    gap: 20px;
  }

  /* ─── CARD ────────────────────────────────────────────────────────────── */
  .hp-l-card {
    position: relative;
    overflow: hidden;
    border-radius: 14px;
    background: var(--hp-navy);
    color: var(--hp-white);
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(10,22,40,0.12);
    transition: transform 0.35s ease, box-shadow 0.35s ease;
    grid-column: span 2;
    grid-row: span 1;
    min-height: 220px;
  }
  .hp-l-card-hero {
    grid-column: span 2;
    grid-row: span 2;
    min-height: 460px;
  }

  /* Photo layer */
  .hp-l-photo {
    position: absolute; inset: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    transition: transform 6s ease;
  }
  /* Scrim — dark gradient bottom-up for text legibility */
  .hp-l-scrim {
    position: absolute; inset: 0;
    background: linear-gradient(
      to top,
      rgba(10,22,40,0.85) 0%,
      rgba(10,22,40,0.55) 28%,
      rgba(10,22,40,0.10) 55%,
      rgba(10,22,40,0.00) 75%
    );
  }

  /* Top-left badge */
  .hp-l-badge {
    position: absolute; top: 14px; left: 14px;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    display: inline-flex; align-items: center; gap: 6px;
    z-index: 2;
  }
  .hp-l-badge-forsale {
    background: var(--hp-blue);
    color: var(--hp-white);
  }
  .hp-l-badge-top {
    background: var(--hp-gold);
    color: var(--hp-navy);
  }
  .hp-l-badge-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--hp-navy);
    display: inline-block;
  }

  /* Top-right photo count pill */
  .hp-l-photo-pill {
    position: absolute; top: 14px; right: 14px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(10,22,40,0.65);
    color: var(--hp-white);
    font-size: 12px; font-weight: 700;
    display: inline-flex; align-items: center; gap: 6px;
    backdrop-filter: blur(4px);
    z-index: 2;
  }
  .hp-l-photo-pill svg {
    width: 14px; height: 14px;
  }

  /* Bottom overlay text stack */
  .hp-l-overlay {
    position: absolute;
    left: 18px; right: 18px; bottom: 16px;
    z-index: 2;
    color: var(--hp-white);
    display: flex; flex-direction: column;
    gap: 4px;
    pointer-events: none;
  }
  .hp-l-price {
    font-size: 28px;
    font-weight: 900;
    letter-spacing: -0.02em;
    color: var(--hp-gold);
    line-height: 1.1;
  }
  .hp-l-card-hero .hp-l-price {
    font-size: 40px;
  }
  .hp-l-office {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.85);
    margin-top: 2px;
  }
  .hp-l-addr {
    font-size: 14px;
    font-weight: 600;
    color: var(--hp-white);
    margin-top: 6px;
    line-height: 1.35;
    text-shadow: 0 1px 2px rgba(0,0,0,0.4);
  }
  .hp-l-card-hero .hp-l-addr {
    font-size: 16px;
  }
  .hp-l-meta {
    font-size: 13px;
    color: rgba(255,255,255,0.92);
    margin-top: 4px;
    display: flex; align-items: center; gap: 8px;
    flex-wrap: wrap;
  }
  .hp-l-meta-sep {
    color: rgba(255,255,255,0.55);
  }

  /* Hover CTA — hidden until card hover */
  .hp-l-cta {
    position: absolute;
    left: 50%;
    bottom: 22px;
    transform: translate(-50%, 16px);
    z-index: 3;
    background: var(--hp-blue);
    color: var(--hp-white);
    border: none;
    border-radius: 999px;
    padding: 10px 22px;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    opacity: 0;
    cursor: pointer;
    transition: opacity 0.25s ease, transform 0.25s ease;
    box-shadow: 0 6px 18px rgba(20,126,251,0.45);
  }

  /* Hover interactions */
  .hp-l-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px rgba(10,22,40,0.22);
  }
  .hp-l-card:hover .hp-l-photo {
    transform: scale(1.06);
  }
  .hp-l-card:hover .hp-l-cta {
    opacity: 1;
    transform: translate(-50%, 0);
  }
  /* When the CTA is visible, nudge the overlay text up so they don't collide */
  .hp-l-card:hover .hp-l-overlay {
    transform: translateY(-12px);
    transition: transform 0.25s ease;
  }
  .hp-l-overlay {
    transition: transform 0.25s ease;
  }

  /* ─── RESPONSIVE ─────────────────────────────────────────────────────── */
  @media (max-width: 900px) {
    .hp-l-section { padding: 40px 0 72px; }
    .hp-l-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      grid-auto-rows: 220px;
      gap: 16px;
    }
    .hp-l-card { grid-column: span 1; min-height: 220px; }
    .hp-l-card-hero {
      grid-column: span 2;
      grid-row: span 1;
      min-height: 320px;
    }
    .hp-l-card-hero .hp-l-price { font-size: 32px; }
  }

  @media (max-width: 560px) {
    .hp-l-section { padding: 32px 0 56px; }
    .hp-container { padding: 0 16px; }
    .hp-l-grid {
      grid-template-columns: 1fr;
      grid-auto-rows: auto;
      gap: 14px;
    }
    .hp-l-card,
    .hp-l-card-hero {
      grid-column: span 1;
      grid-row: span 1;
      min-height: 280px;
    }
    .hp-l-card-hero { min-height: 320px; }
    .hp-l-price { font-size: 24px; }
    .hp-l-card-hero .hp-l-price { font-size: 28px; }
    .hp-l-cta { padding: 8px 18px; font-size: 12px; }
  }

  /* ─── MODAL — BACKDROP + PANEL ───────────────────────────────────────── */
  /* Backdrop is position: fixed but scroll-overflow-y so tall panels still
     scroll inside the viewport (no clipped-content trap). The panel sits
     at the top of the scroll container with comfortable padding. */
  .hp-l-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 200;
    background: rgba(10, 22, 40, 0.78);
    backdrop-filter: blur(6px);
    display: flex;
    justify-content: center;
    align-items: flex-start;
    overflow-y: auto;
    padding: 32px 16px;
    animation: hpLFade 0.18s ease;
  }
  @keyframes hpLFade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .hp-l-modal {
    background: var(--hp-white);
    border-radius: 18px;
    box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5);
    width: 100%;
    max-width: 760px;
    position: relative;
    overflow: hidden;
    animation: hpLSlide 0.22s ease;
  }
  @keyframes hpLSlide {
    from { transform: translateY(8px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
  .hp-l-modal-close {
    position: absolute;
    top: 14px; right: 14px;
    z-index: 5;
    width: 36px; height: 36px;
    border-radius: 999px;
    background: rgba(10, 22, 40, 0.6);
    color: var(--hp-white);
    border: none;
    font-size: 22px;
    line-height: 1;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s ease;
  }
  .hp-l-modal-close:hover { background: rgba(10, 22, 40, 0.85); }

  /* Wrap is the positioned container for the primary photo + arrow overlays
     + counter. Photo div itself stays purely a background-image surface. */
  .hp-l-modal-photo-wrap {
    position: relative;
  }
  .hp-l-modal-photo {
    width: 100%;
    height: 420px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }

  /* Gallery navigation arrows — only rendered post-unlock when there's
     more than one photo to navigate. */
  .hp-l-modal-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 44px; height: 44px;
    border-radius: 999px;
    background: rgba(10, 22, 40, 0.55);
    color: var(--hp-white);
    border: none;
    font-size: 28px;
    line-height: 1;
    font-weight: 700;
    cursor: pointer;
    z-index: 4;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    transition: background 0.15s ease, transform 0.15s ease;
  }
  .hp-l-modal-arrow:hover { background: rgba(10, 22, 40, 0.85); }
  .hp-l-modal-arrow-left { left: 14px; }
  .hp-l-modal-arrow-right { right: 14px; }

  /* Photo counter — small pill bottom-right of the primary photo. */
  .hp-l-modal-photo-counter {
    position: absolute;
    bottom: 14px; right: 14px;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(10, 22, 40, 0.65);
    color: var(--hp-white);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    backdrop-filter: blur(4px);
    z-index: 4;
  }

  /* Photo tease: 7-column grid (6 thumbs + 1 wall) on desktop;
     wraps on mobile. */
  .hp-l-modal-thumbs {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 6px;
    padding: 6px;
    background: var(--hp-bg-soft);
  }
  /* Thumbnails render as <button> elements (clickable after unlock). Reset
     the default button chrome so they look like simple image tiles. */
  .hp-l-thumb {
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-color: #d8d4cc;
    position: relative;
    transition: filter 0.25s ease, opacity 0.25s ease, box-shadow 0.15s ease;
    /* Button resets */
    border: 2px solid transparent;
    padding: 0;
    font: inherit;
    cursor: default;
    display: block;
    box-sizing: border-box;
    outline: none;
  }
  .hp-l-thumb:not(:disabled) { cursor: pointer; }
  .hp-l-thumb:not(:disabled):hover {
    box-shadow: 0 4px 12px rgba(10, 22, 40, 0.18);
  }
  /* (No locked/blurred tile rules — the gate now lives on the wall, not the
     thumbnails. All six thumbs render clear before unlock.) */
  /* Active thumb highlight — inset box-shadow so the highlight doesn't shift
     tile size. Border-color also flips for outline visibility on light bgs. */
  .hp-l-thumb.is-active {
    border-color: var(--hp-blue);
    box-shadow: inset 0 0 0 2px var(--hp-blue), 0 0 0 2px rgba(20, 126, 251, 0.18);
  }
  .hp-l-wall {
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    border: none;
    background: var(--hp-navy);
    color: var(--hp-white);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-align: center;
    padding: 6px;
    cursor: pointer;
    position: relative;
    overflow: hidden;
    transition: background 0.15s ease, transform 0.15s ease, filter 0.25s ease;
  }
  .hp-l-wall::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.06) 0px,
      rgba(255, 255, 255, 0.06) 6px,
      rgba(255, 255, 255, 0) 6px,
      rgba(255, 255, 255, 0) 12px
    );
    filter: blur(1px);
  }
  .hp-l-wall-label {
    position: relative;
    z-index: 1;
    display: inline-block;
    line-height: 1.2;
  }
  .hp-l-wall:hover:not(:disabled) {
    background: #16243a;
    transform: translateY(-1px);
  }
  .hp-l-modal-thumbs.is-unlocked .hp-l-wall {
    background: var(--hp-blue);
    cursor: default;
  }
  .hp-l-modal-thumbs.is-unlocked .hp-l-wall::before { display: none; }

  /* Details */
  .hp-l-modal-details {
    padding: 22px 28px 16px;
  }
  .hp-l-modal-price {
    font-size: 36px;
    font-weight: 900;
    letter-spacing: -0.02em;
    color: var(--hp-navy);
    line-height: 1.1;
  }
  .hp-l-modal-office {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--hp-ink-soft);
    margin-top: 4px;
  }
  .hp-l-modal-addr {
    font-size: 16px;
    font-weight: 600;
    color: var(--hp-ink);
    margin-top: 10px;
    line-height: 1.4;
  }
  .hp-l-modal-city {
    color: var(--hp-ink-soft);
    font-weight: 500;
  }
  .hp-l-modal-meta {
    font-size: 14px;
    color: var(--hp-ink-soft);
    margin-top: 6px;
    display: flex; gap: 8px; align-items: center; flex-wrap: wrap;
  }
  .hp-l-modal-meta-sep { color: #c4c4c4; }

  /* Primary CTA — Request a private showing */
  .hp-l-modal-cta {
    display: block;
    margin: 8px 28px 28px;
    width: calc(100% - 56px);
    background: var(--hp-blue);
    color: var(--hp-white);
    border: none;
    border-radius: 999px;
    padding: 16px 24px;
    font-size: 15px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;
    box-shadow: 0 6px 18px rgba(20,126,251,0.35);
  }
  .hp-l-modal-cta:hover { background: #0c5fc4; transform: translateY(-1px); }

  /* Lead form */
  .hp-l-form {
    padding: 0 28px 28px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .hp-l-form-field { display: flex; flex-direction: column; gap: 4px; }
  .hp-l-form-input {
    width: 100%;
    padding: 13px 14px;
    border: 1px solid var(--hp-line);
    border-radius: 8px;
    font-size: 15px;
    font-family: inherit;
    color: var(--hp-ink);
    background: var(--hp-white);
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }
  .hp-l-form-input:focus {
    border-color: var(--hp-blue);
    box-shadow: 0 0 0 3px rgba(20,126,251,0.15);
  }
  .hp-l-form-input:disabled {
    background: var(--hp-bg-soft);
    color: var(--hp-ink-soft);
    cursor: not-allowed;
  }
  .hp-l-form-err {
    font-size: 12px;
    color: #c92a2a;
    font-weight: 600;
  }
  .hp-l-form-submit {
    margin-top: 4px;
    background: var(--hp-blue);
    color: var(--hp-white);
    border: none;
    border-radius: 999px;
    padding: 15px 24px;
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    cursor: pointer;
    transition: background 0.15s ease, opacity 0.15s ease;
    box-shadow: 0 6px 18px rgba(20,126,251,0.35);
  }
  .hp-l-form-submit:hover:not(:disabled) { background: #0c5fc4; }
  .hp-l-form-submit:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
  }

  /* Success state */
  .hp-l-success {
    margin: 8px 28px 28px;
    padding: 24px;
    background: rgba(20,126,251,0.06);
    border: 1px solid rgba(20,126,251,0.25);
    border-radius: 12px;
    text-align: center;
  }
  .hp-l-success-mark {
    width: 44px; height: 44px;
    border-radius: 999px;
    background: var(--hp-blue);
    color: var(--hp-white);
    margin: 0 auto 10px;
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; font-weight: 800;
  }
  .hp-l-success-title {
    font-size: 17px;
    font-weight: 800;
    color: var(--hp-navy);
    margin-bottom: 4px;
  }
  .hp-l-success-sub {
    font-size: 14px;
    color: var(--hp-ink-soft);
    line-height: 1.5;
  }

  /* Modal responsive */
  @media (max-width: 720px) {
    .hp-l-modal { border-radius: 14px; }
    .hp-l-modal-photo { height: 280px; }
    .hp-l-modal-thumbs {
      grid-template-columns: repeat(4, 1fr);
    }
    .hp-l-modal-details { padding: 18px 20px 12px; }
    .hp-l-modal-price { font-size: 28px; }
    .hp-l-modal-cta,
    .hp-l-form,
    .hp-l-success { margin-left: 20px; margin-right: 20px; }
    .hp-l-modal-cta { width: calc(100% - 40px); }
    .hp-l-form { padding-left: 20px; padding-right: 20px; }
  }
  @media (max-width: 480px) {
    .hp-l-modal-backdrop { padding: 16px 8px; }
    .hp-l-modal-photo { height: 220px; }
    .hp-l-modal-thumbs {
      grid-template-columns: repeat(3, 1fr);
    }
    .hp-l-modal-arrow {
      width: 38px; height: 38px;
      font-size: 22px;
    }
    .hp-l-modal-arrow-left { left: 10px; }
    .hp-l-modal-arrow-right { right: 10px; }
    .hp-l-modal-photo-counter { bottom: 10px; right: 10px; font-size: 11px; padding: 4px 10px; }
  }
`
